'use client';

import { useCallback, useEffect, useState } from 'react';
import { organizerExperienceApi, type OrganizerExperiencePayload } from '@/lib/api';
import { useEventWorkspaceOptional } from '@/components/event-workspace/event-workspace-context';
import { isValidEventWorkspaceId } from '@/lib/resolve-static-param';

function useStandaloneExperience(eventId: string, enabled: boolean) {
  const [experience, setExperience] = useState<OrganizerExperiencePayload | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!enabled || !isValidEventWorkspaceId(eventId)) {
      setExperience(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await organizerExperienceApi.getExperience(eventId);
      setExperience(res.data?.data ?? null);
    } catch {
      setExperience(null);
      setError('Could not load event experience');
    } finally {
      setLoading(false);
    }
  }, [eventId, enabled]);

  useEffect(() => {
    if (enabled) {
      load();
    }
  }, [enabled, load]);

  return { experience, loading, error, refetch: load };
}

export function useOrganizerExperience(eventId: string) {
  const workspace = useEventWorkspaceOptional();
  const usesWorkspace = workspace?.eventId === eventId && isValidEventWorkspaceId(eventId);
  const standalone = useStandaloneExperience(eventId, !usesWorkspace);

  if (usesWorkspace && workspace) {
    return {
      experience: workspace.experience,
      loading: workspace.experienceLoading,
      error: workspace.experienceError,
      refetch: workspace.refetchExperience,
    };
  }

  return standalone;
}
